import { useNavigate, useSearch } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Badge } from "@/components/ui/badge";
import { hubspotDealUrl } from "@/features/cro_forecast/constants";

import {
  useFunnelDetail,
  type KpiFunnelDeal,
  type KpiFunnelDetail,
  type KpiMonthRange,
} from "../api";
import type { KpiSearch } from "..";
import type { KpiPeriod } from "./PeriodSelector";
import { fmtArr, fmtUsd0 } from "../format";

export function FunnelDrawer({
  period,
  range,
  periodLabel,
}: {
  period: KpiPeriod;
  range: KpiMonthRange;
  periodLabel: string;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "funnel";
  const expanded = !!search.expanded;
  const stage = search.stage ?? null;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  const detail = useFunnelDetail(period, range, stage, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, stage: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, stage: undefined, expanded: undefined });
      }}
      title={detail.data?.stage_label ? `Funnel — ${detail.data.stage_label}` : "Funnel"}
      subtitle={periodLabel}
    >
      {detail.isLoading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading deals…</div>
      ) : !detail.data?.available ? (
        <div className="p-6 text-sm text-muted-foreground">No deals for this stage.</div>
      ) : (
        <FunnelView data={detail.data} expanded={expanded} />
      )}
    </EntityDrawer>
  );
}

function FunnelView({ data, expanded }: { data: KpiFunnelDetail; expanded: boolean }) {
  const deals = data.deals ?? [];
  const shown = expanded ? deals : deals.slice(0, 20);
  return (
    <div>
      <div className="border-b border-border p-4">
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
          {data.stage_label} · deals / total amount
        </div>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="text-3xl font-extrabold tabular-nums">{data.count ?? 0}</span>
          <span className="text-lg font-semibold tabular-nums text-muted-foreground">
            {fmtArr(data.total_amount ?? 0, 0)}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left font-semibold">Deal</th>
              {expanded && <th className="px-3 py-2 text-left font-semibold">Owner</th>}
              <th className="px-3 py-2 text-left font-semibold">Outcome</th>
              {expanded && <th className="px-3 py-2 text-left font-semibold">SQO</th>}
              <th className="px-3 py-2 text-right font-semibold">Amount</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <DealRow key={d.deal_id ?? d.deal_name} deal={d} expanded={expanded} />
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={expanded ? 6 : 4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No deals.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!expanded && deals.length > shown.length && (
        <div className="p-4 text-[11px] text-muted-foreground">
          Showing top {shown.length} of {deals.length} by amount — open the full view for all.
        </div>
      )}
    </div>
  );
}

function OutcomeBadge({ deal }: { deal: KpiFunnelDeal }) {
  const o = deal.outcome;
  const variant =
    o === "Won" ? "success" : o === "Lost" ? "danger" : "warning";
  return <Badge variant={variant}>{o ?? "—"}</Badge>;
}

function DealRow({ deal, expanded }: { deal: KpiFunnelDeal; expanded: boolean }) {
  return (
    <tr className="border-b border-border/60 hover:bg-accent/40">
      <td className="px-3 py-2">
        <div className="font-medium">{deal.deal_name}</div>
        {expanded && deal.status && (
          <div className="text-[10px] text-muted-foreground">{deal.status}</div>
        )}
      </td>
      {expanded && (
        <td className="px-3 py-2 text-muted-foreground">{deal.owner ?? "—"}</td>
      )}
      <td className="px-3 py-2">
        <OutcomeBadge deal={deal} />
      </td>
      {expanded && (
        <td className="px-3 py-2 tabular-nums text-muted-foreground">{deal.sqo_date ?? "—"}</td>
      )}
      <td className="px-3 py-2 text-right tabular-nums">{fmtUsd0(deal.amount)}</td>
      <td className="px-2 py-2 text-right">
        {deal.deal_id && (
          <a
            href={hubspotDealUrl(deal.deal_id)}
            target="_blank"
            rel="noreferrer"
            title="Open in HubSpot"
            className="inline-flex text-muted-foreground hover:text-brand"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </td>
    </tr>
  );
}
